import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usePermissions } from '@core/auth/usePermissions';
import {
  ListChecks,
  UserPlus,
  Search,
  Send,
  ChevronDown,
  Check,
  Briefcase,
  TrendingUp,
  Users,
  Building2,
  BarChart3,
  Activity,
  Clock,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@core/auth/useAuth';
import { supabase } from '@core/supabase/client';
import { candidateService } from '@core/supabase/services/candidateService';
import { applicationService } from '@core/supabase/services/applicationService';
import { DashboardFunnelCard } from './components/DashboardFunnelCard';
import { DashboardPositionActivity } from './components/DashboardPositionActivity';
import { DashboardTeamActivity } from './components/DashboardTeamActivity';
import { DashboardDateRangePicker, type DashboardDateRange } from './components/DashboardDateRangePicker';
import { WeeklyChart } from './components/WeeklyChart';
import type { AnalyticsGranularity } from '@core/supabase/services/analyticsWeeklyService';
import { StatusChip } from '@shared/components/ui/StatusChip';
import { SyncStatusBadge } from '@shared/components/SyncStatusBadge';
import { REQUISITION_STATUS_LABELS } from '@core/constants/requisitionLifecycle';
import type { Application, ApplicationStatus, RequisitionStatus } from '@core/types';

// ── Types ─────────────────────────────────────────────────────────────────────

type ViewMode = 'summary' | 'pipeline';

type RecentReq = {
  id: string;
  title: string;
  status: RequisitionStatus;
  updated_at: string;
  headcount: number;
  priority: string | null;
  department: string | null;
  location: string | null;
  employment_type: string | null;
  business_justification: string | null;
  created_by: string | null;
  job_id: string | null;
  client: { id: string; name: string } | null;
};

const SHORTLISTED_STATUSES: ApplicationStatus[] = [
  'shortlisted',
  'interview_1_scheduled',
  'interview_1_conducted',
  'interview_2_scheduled',
  'interview_2_conducted',
  'offer_extended',
  'offer_accepted',
  'offer_rejected',
  'placed',
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const REQ_STATUS_DOT: Record<RequisitionStatus, string> = {
  sourcing: '#10B981',
  approved: '#10B981',
  filled: '#10B981',
  submitted: '#3B82F6',
  awaiting_client: '#F59E0B',
  on_hold: '#9CA3AF',
  draft: '#9CA3AF',
  rejected: '#EF4444',
  cancelled: '#EF4444',
};

function timeAgo(isoString: string): string {
  const diffMs = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function nameInitials(name?: string | null): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const OUTREACH_STATUSES: ApplicationStatus[] = [
  'interview_1_scheduled',
  'interview_1_conducted',
  'interview_2_scheduled',
  'interview_2_conducted',
  'offer_extended',
  'offer_accepted',
  'offer_rejected',
  'placed',
];

// ── Page ──────────────────────────────────────────────────────────────────────

export function DashboardAnalyticsPage() {
  const { user } = useAuth();
  const orgId = user?.orgId ?? null;
  const [range, setRange] = useState<DashboardDateRange>('30d');
  const [view, setView] = useState<ViewMode>('summary');

  if (!orgId) {
    return (
      <div className="rounded-xl border vscode-border p-8 vscode-bg-secondary text-center">
        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(59, 130, 246, 0.15)' }}>
          <BarChart3 className="w-6 h-6 text-blue-500" />
        </div>
        <p className="text-base font-medium vscode-text-primary mb-1">No analytics available</p>
        <p className="text-sm vscode-text-tertiary">Join an organisation to see analytics.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-0 w-full max-w-[1280px] flex-col gap-6 px-4">
      {/* ── Header with controls ─────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold vscode-text-primary">Dashboard</h1>
          <p className="text-sm vscode-text-tertiary">Track your recruiting performance and pipeline</p>
        </div>
        <div className="flex items-center gap-2">
          <ViewSelector value={view} onChange={setView} />
          <DashboardDateRangePicker value={range} onChange={setRange} />
        </div>
      </div>

      {/* ── Body ────────────────────────────────────────────────────── */}
      {view === 'summary' ? (
        <SummaryView orgId={orgId} range={range} />
      ) : (
        <PipelineView orgId={orgId} range={range} />
      )}
    </div>
  );
}

export default DashboardAnalyticsPage;

// ── View selector ─────────────────────────────────────────────────────────────

function ViewSelector({
  value,
  onChange,
}: {
  value: ViewMode;
  onChange: (v: ViewMode) => void;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener('mousedown', onDoc);
    return () => window.removeEventListener('mousedown', onDoc);
  }, [open]);

  const OPTIONS: { id: ViewMode; label: string; icon: typeof BarChart3 }[] = [
    { id: 'summary', label: 'Summary', icon: BarChart3 },
    { id: 'pipeline', label: 'Pipeline', icon: TrendingUp },
  ];

  const currentOpt = OPTIONS.find((o) => o.id === value);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-xl border vscode-border vscode-bg-secondary px-4 py-2 text-sm vscode-text-secondary transition-all hover:border-emerald-500/50 hover:shadow-sm"
      >
        {currentOpt && <currentOpt.icon className="h-4 w-4" />}
        {value === 'summary' ? 'Summary' : 'Pipeline'}
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden />
      </button>
      {open && (
        <div
          ref={menuRef}
          className="absolute right-0 top-full z-20 mt-2 w-44 overflow-hidden rounded-xl border vscode-border vscode-bg-elevated shadow-xl"
        >
          {OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => {
                onChange(opt.id);
                setOpen(false);
                btnRef.current?.focus();
              }}
              className={`flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition-colors ${
                opt.id === value
                  ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400'
                  : 'vscode-text-secondary hover:vscode-bg-hover'
              }`}
            >
              <opt.icon className="h-4 w-4" />
              {opt.label}
              {opt.id === value && <Check className="h-4 w-4 ml-auto" aria-hidden />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Summary view (3-row layout) ───────────────────────────────────────────────

function SummaryView({ orgId, range }: { orgId: string; range: DashboardDateRange }) {
  const { user } = useAuth();
  const [chartGranularity, setChartGranularity] = useState<AnalyticsGranularity>('weekly');

  // Shared queries — same keys as DashboardKpiRow to hit the cache
  const candidatesQ = useQuery({
    queryKey: ['dashboardAnalytics', 'candidates-all', orgId],
    enabled: Boolean(orgId),
    queryFn: async () => {
      const result = await candidateService.list(orgId, { pageSize: 500, page: 1 });
      return Array.isArray(result) ? result : result.data;
    },
    staleTime: 60_000,
  });

  const applicationsQ = useQuery({
    queryKey: ['dashboardAnalytics', 'applications-all', orgId],
    enabled: Boolean(orgId),
    queryFn: async () => applicationService.list(orgId, { limit: 500 }),
    staleTime: 60_000,
  });

  const recentReqsQ = useQuery({
    queryKey: ['dashboard', 'recentReqs', orgId],
    enabled: Boolean(orgId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ggj_b2b_requisitions')
        .select(
          'id, title, status, updated_at, headcount, priority, department, location, employment_type, business_justification, created_by, job_id, client:ggj_b2b_clients(id, name)',
        )
        .eq('org_id', orgId)
        .order('updated_at', { ascending: false })
        .limit(4);
      if (error) throw error;
      return (data ?? []) as RecentReq[];
    },
    staleTime: 60_000,
  });

  const shortlistCountByJob = useMemo(() => {
    const map = new Map<string, { shortlisted: number; saved: number }>();
    for (const a of applicationsQ.data ?? []) {
      const app = a as Application;
      if (!app.job_id) continue;
      const entry = map.get(app.job_id) ?? { shortlisted: 0, saved: 0 };
      entry.saved += 1;
      if ((SHORTLISTED_STATUSES as string[]).includes(app.status)) {
        entry.shortlisted += 1;
      }
      map.set(app.job_id, entry);
    }
    return map;
  }, [applicationsQ.data]);

  // KPI derivations from existing data
  const { totalLists, candidatesSaved, totalSearches, candidatesOutreached } = useMemo(() => {
    const apps = applicationsQ.data ?? [];
    const cands = candidatesQ.data ?? [];
    return {
      totalLists: new Set(apps.map((a) => (a as { job_id?: string }).job_id).filter(Boolean)).size,
      candidatesSaved: cands.length,
      totalSearches: apps.length,
      candidatesOutreached: apps.filter((a) =>
        (OUTREACH_STATUSES as string[]).includes(a.status),
      ).length,
    };
  }, [applicationsQ.data, candidatesQ.data]);

  const loading = candidatesQ.isLoading || applicationsQ.isLoading;

  // "Profiles added today" — candidates created since midnight local time
  const todayProfiles = useMemo(() => {
    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);
    const cutoff = midnight.getTime();
    return (candidatesQ.data ?? []).filter((c) => {
      const t = Date.parse((c as { created_at?: string }).created_at ?? '');
      return Number.isFinite(t) && t >= cutoff;
    });
  }, [candidatesQ.data]);

  const currentUserName = useMemo(() => {
    const anyUser = user as unknown as
      | { name?: string; fullName?: string; displayName?: string; email?: string }
      | null;
    if (!anyUser) return 'Team member';
    return (
      anyUser.fullName ||
      anyUser.name ||
      anyUser.displayName ||
      (anyUser.email ? anyUser.email.split('@')[0] : 'Team member')
    );
  }, [user]);

  return (
    <div className="flex flex-col gap-8">
      <SyncStatusBadge />
      
      {/* ── Row 1: KPI tiles (moved up for prominence) ─────────────────────────────────────── */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1 h-5 rounded-full bg-emerald-500" />
          <p className="text-xs font-semibold uppercase tracking-wider vscode-text-tertiary">
            Overview
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KpiTile
            icon={<ListChecks className="h-5 w-5" aria-hidden />}
            iconBg="rgba(16, 185, 129, 0.15)"
            iconColor="#10B981"
            label="Total Lists"
            value={loading ? null : totalLists}
          />
          <KpiTile
            icon={<UserPlus className="h-5 w-5" aria-hidden />}
            iconBg="rgba(59, 130, 246, 0.15)"
            iconColor="#3B82F6"
            label="Candidates Saved"
            value={loading ? null : candidatesSaved}
          />
          <KpiTile
            icon={<Search className="h-5 w-5" aria-hidden />}
            iconBg="rgba(139, 92, 246, 0.15)"
            iconColor="#8B5CF6"
            label="Total Searches"
            value={loading ? null : totalSearches}
          />
          <KpiTile
            icon={<Send className="h-5 w-5" aria-hidden />}
            iconBg="rgba(245, 158, 11, 0.15)"
            iconColor="#F59E0B"
            label="Candidates Outreached"
            value={loading ? null : candidatesOutreached}
          />
        </div>
      </section>

      {/* ── Row 2: Recent searches ───────────────────────────────── */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1 h-5 rounded-full bg-blue-500" />
          <p className="text-xs font-semibold uppercase tracking-wider vscode-text-tertiary">
            Recent searches
          </p>
        </div>
        <RecentSearchesRow
          reqs={recentReqsQ.data ?? []}
          loading={recentReqsQ.isLoading}
          shortlistCountByJob={shortlistCountByJob}
          currentUserName={currentUserName}
        />
      </section>

      {/* ── Row 3: Dual pane ─────────────────────────────────────── */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1 h-5 rounded-full bg-purple-500" />
          <p className="text-xs font-semibold uppercase tracking-wider vscode-text-tertiary">
            Activity
          </p>
        </div>
        <div className="flex flex-col gap-4 lg:flex-row">
          {/* Left 42%: Profiles added today */}
          <div className="flex flex-col rounded-xl border vscode-border vscode-bg-secondary overflow-hidden lg:w-[42%]">
            <div className="border-b vscode-border px-5 py-4" style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(59, 130, 246, 0.05) 100%)' }}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(16, 185, 129, 0.15)' }}>
                    <Users className="h-5 w-5 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold vscode-text-primary">Profiles added today</p>
                    <p className="text-xs vscode-text-tertiary">
                      {loading ? '…' : todayProfiles.length === 0 ? 'None added yet' : `${todayProfiles.length} profile${todayProfiles.length !== 1 ? 's' : ''} since midnight`}
                    </p>
                  </div>
                </div>
                {!loading && todayProfiles.length > 0 && (
                  <span className="rounded-full px-3 py-1 text-sm font-bold text-emerald-600" style={{ background: 'rgba(16, 185, 129, 0.15)' }}>
                    {todayProfiles.length}
                  </span>
                )}
              </div>
            </div>
            <div className="flex max-h-[380px] min-h-[220px] flex-col divide-y vscode-border overflow-y-auto">
              {candidatesQ.isLoading ? (
                <ProfileSkeleton />
              ) : todayProfiles.length === 0 ? (
                <div className="flex flex-1 flex-col items-center justify-center p-6 text-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{ background: 'rgba(156, 163, 175, 0.15)' }}>
                    <Users className="w-5 h-5 vscode-text-tertiary" />
                  </div>
                  <p className="text-sm vscode-text-tertiary">No profiles added today.</p>
                </div>
              ) : (
                todayProfiles.map((c) => {
                  const cand = c as {
                    id: string;
                    full_name?: string;
                    email?: string;
                    current_title?: string;
                    created_at?: string;
                  };
                  const name = cand.full_name || cand.email || 'Unknown';
                  const addedTime = cand.created_at
                    ? new Date(cand.created_at).toLocaleTimeString(undefined, {
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : null;
                  return (
                    <div key={cand.id} className="flex items-center gap-3 px-5 py-3 hover:vscode-bg-hover transition-colors">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xs font-semibold" style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: 'white' }}>
                        {nameInitials(cand.full_name)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium vscode-text-primary">{name}</p>
                        <p className="truncate text-xs vscode-text-tertiary">
                          {cand.current_title || cand.email || ''}
                        </p>
                      </div>
                      {addedTime && (
                        <span className="shrink-0 flex items-center gap-1 text-xs vscode-text-tertiary">
                          <Clock className="w-3 h-3" />
                          {addedTime}
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Right 64%: Applications added — live time-series chart */}
          <div className="flex flex-1 flex-col rounded-xl border vscode-border vscode-bg-secondary overflow-hidden">
            <div className="flex items-center justify-between gap-2 border-b vscode-border px-5 py-4" style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(59, 130, 246, 0.15)' }}>
                  <Activity className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold vscode-text-primary">Applications over time</p>
                  <p className="text-xs vscode-text-tertiary">Track your application volume trends</p>
                </div>
              </div>
              <ChartToggle value={chartGranularity} onChange={setChartGranularity} />
            </div>
            <div className="flex flex-1 items-end gap-2 p-5">
              <WeeklyChart
                orgId={orgId}
                metric="applications"
                range={range}
                granularity={chartGranularity}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Row 4: Pipeline Insights (cascade / splice) ──────────── */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1 h-5 rounded-full bg-amber-500" />
          <p className="text-xs font-semibold uppercase tracking-wider vscode-text-tertiary">
            Pipeline Insights
          </p>
        </div>
        <DashboardFunnelCard orgId={orgId} range={range} />
      </section>
    </div>
  );
}

// ── Pipeline view ─────────────────────────────────────────────────────────────

function PipelineView({ orgId, range }: { orgId: string; range: DashboardDateRange }) {
  const perms = usePermissions();
  const canSeeActivityPanels = perms.canViewReports;

  return (
    <div className="flex flex-col gap-5">
      <DashboardFunnelCard orgId={orgId} range={range} />
      {canSeeActivityPanels ? (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <DashboardPositionActivity orgId={orgId} />
          <DashboardTeamActivity orgId={orgId} />
        </div>
      ) : null}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function buildReqDescription(req: RecentReq): string {
  const trimmed = (req.business_justification ?? '').trim();
  if (trimmed) return trimmed;
  const bits: string[] = [];
  bits.push(`${req.title}${req.headcount > 1 ? ` (${req.headcount} roles)` : ''}`);
  const ctx: string[] = [];
  if (req.department) ctx.push(req.department);
  if (req.location) ctx.push(req.location);
  if (req.employment_type) ctx.push(req.employment_type);
  if (ctx.length) bits.push(ctx.join(' · '));
  if (req.priority && req.priority !== 'normal') bits.push(`${req.priority} priority`);
  return bits.join(' — ');
}

function reqTypeLabel(status: RequisitionStatus): string {
  if (status === 'sourcing') return 'Sourcing';
  if (status === 'filled') return 'Filled';
  if (status === 'approved') return 'Collection';
  if (status === 'submitted') return 'Pending';
  if (status === 'on_hold') return 'On hold';
  if (status === 'cancelled' || status === 'rejected') return 'Closed';
  if (status === 'draft') return 'Draft';
  return 'Active';
}

function RecentSearchesRow({
  reqs,
  loading,
  shortlistCountByJob,
  currentUserName,
}: {
  reqs: RecentReq[];
  loading: boolean;
  shortlistCountByJob: Map<string, { shortlisted: number; saved: number }>;
  currentUserName: string;
}) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-[200px] animate-pulse rounded-xl vscode-bg-tertiary"
          />
        ))}
      </div>
    );
  }

  if (reqs.length === 0) {
    return (
      <div className="flex flex-col items-center gap-4 rounded-xl border vscode-border vscode-bg-secondary py-10">
        <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: 'rgba(59, 130, 246, 0.15)' }}>
          <Search className="w-6 h-6 text-blue-500" />
        </div>
        <p className="text-sm vscode-text-tertiary">No searches yet. Start your first search.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {reqs.map((req) => {
        const counts = req.job_id ? shortlistCountByJob.get(req.job_id) : undefined;
        const shortlistedN = counts?.shortlisted ?? 0;
        const savedN = counts?.saved ?? 0;
        const candidatesLabel =
          shortlistedN > 0
            ? `${shortlistedN} shortlisted`
            : savedN > 0
              ? `${savedN} saved`
              : '0 saved';
        const description = buildReqDescription(req);
        const isActive =
          req.status === 'sourcing' ||
          req.status === 'approved' ||
          req.status === 'submitted';
        const updated = timeAgo(req.updated_at);
        const creatorRaw = (req.created_by ?? '').trim();
        const creatorName = creatorRaw
          ? creatorRaw.includes('@')
            ? creatorRaw.split('@')[0]
            : creatorRaw.length > 30
              ? currentUserName
              : creatorRaw
          : currentUserName;
        return (
          <button
            key={req.id}
            type="button"
            onClick={() => navigate(`/requisitions/${req.id}`)}
            className="group flex min-h-[200px] flex-col rounded-xl border vscode-border vscode-bg-secondary p-4 text-left transition-all hover:border-emerald-500/50 hover:shadow-lg"
          >
            {/* Header: icon + title */}
            <div className="flex items-start gap-3 mb-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg" style={{ background: 'rgba(59, 130, 246, 0.15)' }}>
                <Briefcase className="h-5 w-5 text-blue-500" aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold vscode-text-primary group-hover:text-emerald-500 transition-colors">
                  {req.title}
                </span>
                {req.client?.name && (
                  <span className="flex items-center gap-1 mt-0.5 text-xs vscode-text-tertiary">
                    <Building2 className="h-3 w-3" />
                    <span className="truncate">{req.client.name}</span>
                  </span>
                )}
              </div>
            </div>

            {/* Description */}
            <p className="line-clamp-2 text-xs leading-relaxed vscode-text-tertiary mb-3">
              {savedN === 0 && shortlistedN === 0
                ? `Not shared · ${updated}`
                : description}
            </p>

            {/* Stats row */}
            <div className="flex items-center gap-3 mb-3">
              {isActive ? (
                <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10B981' }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Active
                </span>
              ) : (
                <StatusChip
                  label={REQUISITION_STATUS_LABELS[req.status] ?? req.status}
                  dotColor={REQ_STATUS_DOT[req.status] ?? '#9CA3AF'}
                />
              )}
              <span className="text-xs vscode-text-secondary">
                <span className="font-medium">{candidatesLabel}</span>
              </span>
            </div>

            {/* Footer */}
            <div className="mt-auto flex items-center gap-2 border-t vscode-border pt-3">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold" style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)', color: 'white' }}>
                {nameInitials(creatorName)}
              </span>
              <span className="truncate text-xs vscode-text-tertiary">
                {creatorName}
              </span>
              <span className="ml-auto text-xs vscode-text-tertiary">{updated}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function KpiTile({
  icon,
  iconBg,
  iconColor,
  label,
  value,
}: {
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  label: string;
  value: number | null;
}) {
  return (
    <div className="group relative flex flex-col rounded-xl border vscode-border vscode-bg-secondary p-5 transition-all hover:shadow-lg hover:border-emerald-500/30">
      {/* Icon chip */}
      <div
        className="flex h-11 w-11 items-center justify-center rounded-xl mb-4"
        style={{ background: iconBg, color: iconColor }}
      >
        {icon}
      </div>
      {/* Number */}
      {value === null ? (
        <div className="h-9 w-16 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700 mb-2" />
      ) : (
        <span
          className="text-3xl font-bold leading-none tabular-nums mb-2"
          style={{ color: value > 0 ? iconColor : 'var(--vscode-text-tertiary)' }}
        >
          {value.toLocaleString()}
        </span>
      )}
      {/* Label */}
      <span className="text-sm vscode-text-secondary">{label}</span>
      {/* Subtle gradient overlay on hover */}
      <div
        className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
        style={{ background: `linear-gradient(135deg, ${iconBg} 0%, transparent 60%)` }}
      />
    </div>
  );
}

function ChartToggle({
  value,
  onChange,
}: {
  value: AnalyticsGranularity;
  onChange: (next: AnalyticsGranularity) => void;
}) {
  const OPTIONS: { id: AnalyticsGranularity; label: string }[] = [
    { id: 'weekly', label: 'Weekly' },
    { id: 'monthly', label: 'Monthly' },
  ];
  return (
    <div className="flex items-center overflow-hidden rounded-lg border vscode-border text-xs">
      {OPTIONS.map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onChange(opt.id)}
          className={`px-3 py-1.5 font-medium transition-all ${
            value === opt.id
              ? 'text-white'
              : 'vscode-text-secondary hover:vscode-bg-hover'
          }`}
          style={value === opt.id ? { background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)' } : undefined}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="space-y-1 p-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-3 py-1.5">
          <div className="h-8 w-8 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
          <div className="flex-1 space-y-1">
            <div className="h-3 w-3/4 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-2.5 w-1/2 animate-pulse rounded bg-gray-100 dark:bg-gray-800" />
          </div>
        </div>
      ))}
    </div>
  );
}
